import { AuthedButton } from '../authed-button';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { IS_DEBUG } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { useModelStore } from '@/client/store/model-store';
import { cn, getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { handledKeyDownCallBackEnter } from '@/client/utils/handle';
import { refetchComments } from '@/client/utils/query-status';
import { SmilePlusIcon } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const LazyEmojiPicker = lazy(() =>
  Promise.all([import('@emoji-mart/react'), import('@emoji-mart/data')]).then(
    ([{ default: Picker }, { default: data }]) => ({
      default: (props: { onEmojiSelect: (emoji: { native: string; shortcodes: string; unified: string }) => void; theme: string }) => (
        <Picker data={data} {...props} />
      ),
    })
  )
);

type ReplyInputProps = {
  className?: string;
  postUuid?: string;
  rootUuid?: string;
  parentUuid?: string;
  open?: boolean;
  autoOpen?: boolean;
  autoClose?: boolean;
  userUid?: string;
  userName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onCached?: () => void;
  onCallback?: (success: boolean) => void;
};
export const ReplyInput = ({
  className,
  open,
  postUuid,
  rootUuid,
  parentUuid,
  autoOpen = true,
  autoClose = true,
  onFocus,
  onBlur,
  onCallback,
  onCached,
}: ReplyInputProps) => {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');
  const repliedParams = useModelStore(state => state.repliedParams);
  const setRepliedParams = useModelStore(state => state.setRepliedParams);
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const [isEdit, setIsEdit] = useState(open ?? false);
  const [isLoading, setIsLoading] = useState(false);
  const handleReply = async () => {
    if (inputText.length === 0 || inputText.length > 300) return;
    setIsLoading(true);
    try {
      await api.community.publishComment(
        { baseTokenAddress },
        {
          content: inputText,
          postUuid,
          rootUuid,
          parentUuid,
        }
      );
      await refetchComments();
      setRepliedParams({
        ...repliedParams,
        total: repliedParams.total + 1,
      });
      setInputText('');
      onCallback?.(true);
      toast.success(t('reply-successfully'));
    } catch (error) {
      const msg = getErrorMessage(error);
      onCallback?.(false);
      if (IS_DEBUG) console.log('replay error', error);
      toast.error(msg ?? t('reply-failed'));
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (open !== undefined) {
      setIsEdit(open);
    }
  }, [open]);
  return (
    <div
      className={cn(
        'w-full p-4 text-base font-medium leading-5 border border-foreground/4 shadow-sm rounded-3 flex flex-col',
        'transition-all duration-300 ease-in-out',
        inputText.length !== 0 || isEdit ? 'min-h-32 bg-foreground/4' : 'h-13 overflow-hidden',
        className
      )}
    >
      <textarea
        spellCheck={false}
        onFocus={() => {
          if (autoOpen) setIsEdit(true);
          onFocus?.();
        }}
        onBlur={() => {
          if (autoClose && inputText.length > 0) setIsEdit(false);
          onBlur?.();
        }}
        onKeyDown={e => {
          if (!isLoading) {
            handledKeyDownCallBackEnter(handleReply)(e);
          }
        }}
        value={inputText}
        onChange={e => setInputText(e.target.value)}
        className="resize-none w-full bg-transparent flex-1 min-h-10 placeholder:text-foreground/40 text-foreground"
        placeholder="Say something"
      />
      <div className="flex items-center justify-between mt-2 ">
        <div className="flex items-center">
          <Button
            onClick={() => {
              setIsEdit(false);
              setInputText('');
              onCached?.();
            }}
            size="sm"
            variant="ghost"
            className="text-sm text-foreground/70 text-xs leading-none shadow-none px-2"
          >
            {t('cache')}
          </Button>
          <Popover>
            <PopoverTrigger>
              <Button variant="ghost" size="icon">
                <SmilePlusIcon className="size-4 stroke-foreground/70" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="relative w-full p-0 border-none shadow-none">
              <Suspense fallback={<div className="w-[352px] h-[435px] flex items-center justify-center">Loading...</div>}>
                <LazyEmojiPicker
                  onEmojiSelect={(emoji: { native: string; shortcodes: string; unified: string }) => {
                    setInputText(inputText + emoji.native || emoji.shortcodes || emoji.unified);
                  }}
                  theme="light"
                />
              </Suspense>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs leading-none text-foreground/40">
            <span className={inputText.length > 300 ? 'text-[#F95E5E]' : ''}>{inputText.length}</span> / 300
          </div>
          <AuthedButton
            showAuth={false}
            size="sm"
            variant="outline"
            isLoading={isLoading}
            disabled={inputText.length === 0 || inputText.length > 300}
            onClick={handleReply}
            className="shadow-none bg-card/4 text-xs leading-none rounded-full disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none "
          >
            {t('reply')}
          </AuthedButton>
        </div>
      </div>
    </div>
  );
};
